import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import TopContentService from "../../components/Service/ServiceContentComponents/TopContentService";
import {
  FaArrowLeft,
  FaRegEdit,
  FaEye,
  FaUsers,
  FaClock,
  FaTag,
} from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { HiOutlineSparkles } from "react-icons/hi";
import toast from "react-hot-toast";
import DeleteSeriveModal from "../../components/AdminSideComponents/Modals/DeleteServiceModal";
const GalleryGrid = ({ galleryItems }) => {
  const [selectedImg, setSelectedImg] = useState(null);

  const closeModal = () => setSelectedImg(null);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        {galleryItems?.map((img, idx) => (
          <motion.div
            key={idx}
            className="cursor-pointer overflow-hidden rounded-lg shadow-md"
            onClick={() => setSelectedImg(img)}
            whileHover={{ scale: 1.02 }}
          >
            <img
              src={img}
              alt="Gallery"
              className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
            />
          </motion.div>
        ))}
      </div>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="relative max-w-6xl w-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute -top-12 right-0 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full transition-colors duration-200 z-10"
              >
                <X size={24} />
              </button>
              <img
                src={selectedImg}
                alt="Preview"
                className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MiddleContentService = ({ service }) => {
  const images = service.images?.slice(1) || []; // Skip first, used in top

  return (
    <div className="mt-6">
      {service.description && service.description.length > 0 ? (
        <div className="space-y-8">
          {service.description.map((desc, index) => (
            <div key={index}>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {desc.heading}
              </h2>
              <p className="text-gray-700 leading-7 whitespace-pre-wrap">
                {desc.content}
              </p>
              {index < service.description.length - 1 && (
                <div className="border-b border-gray-300 md:my-8"></div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📝</div>
          <h3 className="text-gray-600 font-semibold text-xl mb-2">
            No Description Available
          </h3>
        </div>
      )}

      <GalleryGrid galleryItems={images} />
    </div>
  );
};

const ServiceDonationProcess = () => {
  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4 text-gray-900">
          Donation Process
        </h3>
        <p className="text-gray-700 leading-7">
          This section Will hold the donation modal
        </p>
      </div>
    </div>
  );
};

const AdminServiceDetails = () => {
  const { id: serviceId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!serviceId) return;
    axiosInstance
      .get(`/services/service-by-id/${serviceId}`)
      .then((res) => setService(res.data))
      .catch((err) => console.error("Error fetching service:", err))
      .finally(() => setLoading(false));
  }, [serviceId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const handleDeleteService = async () => {
    setDeleteLoading(true);
    const deletingToast = toast.loading("Deleting service...");
    try {
      await axiosInstance.delete(`/services/delete-service/${serviceId}`);
      toast.success("Service deleted successfully", { id: deletingToast });
      navigate("/admin/services"); // Redirect back to services list
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service. Please try again.", {
        id: deletingToast,
      });
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header skeleton */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="flex space-x-3">
              <div className="w-24 h-10 bg-gray-200 rounded-lg animate-pulse" />
              <div className="w-24 h-10 bg-gray-200 rounded-lg animate-pulse" />
            </div>
          </div>

          {/* Content skeleton */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="h-96 bg-gray-200 animate-pulse" />
            <div className="p-8 space-y-6">
              <div className="h-8 w-3/4 bg-gray-200 rounded animate-pulse" />
              <div className="space-y-3">
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-12 max-w-md mx-auto">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-red-600 font-semibold text-xl mb-4">
              Service Not Found
            </h3>
            <p className="text-red-500 mb-6">
              The service you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
            >
              Back to Services
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      {/* Header with navigation and actions */}
      <div className="flex mt-10 flex-col sm:flex-row items-start pr-8 sm:items-center justify-between mb-8 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center ml-4 justify-center w-12 h-12 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
          >
            <FaArrowLeft className="text-gray-600 group-hover:text-gray-800 transition-colors duration-200" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              Service Preview
            </h1>
            <p className="text-gray-600">How this service appears to users</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/edit-admin-service/${serviceId}`)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <FaRegEdit className="text-sm" />
            <span>Edit Service</span>
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <MdDeleteOutline className="text-lg" />
            <span>Delete</span>
          </button>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row w-full justify-center gap-6 md:px-6 px-4 py-6">
        {/* Left Section */}
        <div className="w-full lg:w-[65%] xl:w-[70%]">
          <TopContentService service={service} />
          <MiddleContentService service={service} />
        </div>

        {/* Right Section (Donation) */}
        <div className="w-full lg:w-[27%]">
          <div className="sticky top-40">
            <ServiceDonationProcess />
          </div>
        </div>
        {isDeleteModalOpen && (
          <DeleteSeriveModal
            itemName={service.serviceName}
            loading={deleteLoading}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={handleDeleteService}
          />
        )}
      </div>
    </div>
  );
};

export default AdminServiceDetails;
